import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_140bf421-cbc4-4de3-8362-159a067ec55a/artifacts/r2lyf9g6_golden-tree-events-logo.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about-us' },
  { name: 'Our Events', path: '/events' },
  { 
    name: 'Award Fee', 
    path: '#',
    submenu: [
      { name: 'Registration Fee', path: '/registration-fee' },
      { name: 'Winner Package Pricing', path: '/winner-package' },
    ]
  },
  { name: 'Clients', path: '/clients' },
  { name: 'Testimonials', path: '/testimonials' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact Us', path: '/contact-us' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-black py-3'
          : 'bg-black py-5'
      }`}
    >
      <div className="w-full px-4 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="Golden Tree Events"
              className="w-auto"
              style={{ height: '4rem' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-6">
            {navLinks.map((link) => (
              link.submenu ? (
                <div key={link.name} className="relative group">
                  <button
                    className="font-google-sans text-sm tracking-wide uppercase transition-colors duration-300 whitespace-nowrap text-white hover:text-[#D4AF37] flex items-center gap-1 font-medium"
                    onClick={() => toggleDropdown(link.name)}
                    data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {/* Dropdown */}
                  <div className="absolute top-full left-0 mt-2 w-56 glass opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    {link.submenu.map((sublink) => (
                      <Link
                        key={sublink.path}
                        to={sublink.path}
                        data-testid={`nav-sublink-${sublink.name.toLowerCase().replace(/\s/g, '-')}`}
                        className={`block px-4 py-3 text-sm uppercase tracking-wide transition-colors duration-300 ${
                          location.pathname === sublink.path
                            ? 'text-[#D4AF37] bg-white/5'
                            : 'text-white hover:text-[#D4AF37] hover:bg-white/5'
                        }`}
                      >
                        {sublink.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`font-google-sans text-sm tracking-wide uppercase transition-colors duration-300 whitespace-nowrap font-medium ${
                    location.pathname === link.path
                      ? 'text-[#D4AF37]'
                      : 'text-white hover:text-[#D4AF37]'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden xl:block">
            <a
              href="https://goldentreeawards.com/award-nomination"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nominate-now-btn"
              className="bg-[#D4AF37] text-black font-semibold px-6 py-3 uppercase tracking-widest text-sm hover:bg-[#C5A028] transition-all duration-300"
            >
              Register Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`xl:hidden transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="glass rounded-lg p-4 space-y-2">
            {navLinks.map((link) => (
              link.submenu ? (
                <div key={link.name}>
                  <button
                    onClick={() => toggleDropdown(link.name)}
                    className="w-full flex items-center justify-between py-2 text-sm uppercase tracking-wide text-white/80"
                  >
                    {link.name}
                    <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openDropdown === link.name ? 'max-h-40' : 'max-h-0'}`}>
                    {link.submenu.map((sublink) => (
                      <Link
                        key={sublink.path}
                        to={sublink.path}
                        className={`block py-2 pl-4 text-sm uppercase tracking-wide ${
                          location.pathname === sublink.path ? 'text-[#D4AF37]' : 'text-white/60'
                        }`}
                      >
                        {sublink.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`block py-2 text-sm uppercase tracking-wide ${
                    location.pathname === link.path
                      ? 'text-[#D4AF37]'
                      : 'text-white/80'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
            <a
              href="https://goldentreeawards.com/award-nomination"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-register-btn"
              className="block bg-[#D4AF37] text-black text-center font-medium py-3 uppercase tracking-widest text-xs mt-4"
            >
              Register Now
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};
